import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { ApiError } from '../../../lib/errors';
import { rpaRepository as repo } from '../../../lib/repositories/rpa.repository';
import type { DanhSachUserOpenCore, RoleOpenCore, ThanRoleOpenCore, ThanUserOpenCore, UserOpenCore } from '../types';
import { KHOA_RPA, thuLai } from './useRpa';

/** User OpenCore của tenant (tối đa 500), kèm role và cờ bảo vệ. Màn License cũng đọc để ghép User chưa có License. */
export function useUsersOpenCore(bat = true) {
  return useQuery<DanhSachUserOpenCore, ApiError>({
    queryKey: KHOA_RPA.usersOpenCore,
    queryFn: () => repo.usersOpenCore(),
    enabled: bat,
    staleTime: 15_000,
    retry: thuLai,
  });
}

export function useRolesOpenCore(bat = true) {
  return useQuery<RoleOpenCore[], ApiError>({
    queryKey: KHOA_RPA.rolesOpenCore,
    queryFn: () => repo.rolesOpenCore(),
    enabled: bat,
    staleTime: 15_000,
    retry: thuLai,
  });
}

/**
 * User và role nằm trên cùng một tài liệu OpenCore (`members` của role): sửa một bên là bên kia đổi. Khoá User còn
 * đồng bộ lại License của User đó, nên làm mới cả License.
 */
function useLamMoiDanhBa() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: KHOA_RPA.danhBaOpenCore });
    void qc.invalidateQueries({ queryKey: KHOA_RPA.licenses });
  };
}

export function useTaoUserOpenCore() {
  const lamMoi = useLamMoiDanhBa();
  return useMutation<UserOpenCore, ApiError, ThanUserOpenCore>({ mutationFn: (than) => repo.taoUserOpenCore(than), onSettled: lamMoi });
}

export function useSuaUserOpenCore() {
  const lamMoi = useLamMoiDanhBa();
  return useMutation<UserOpenCore, ApiError, { id: string; than: ThanUserOpenCore }>({
    mutationFn: ({ id, than }) => repo.suaUserOpenCore(id, than),
    onSettled: lamMoi,
  });
}

export function useKhoaUserOpenCore() {
  const lamMoi = useLamMoiDanhBa();
  return useMutation<UserOpenCore, ApiError, { id: string; khoa: boolean }>({
    mutationFn: ({ id, khoa }) => repo.khoaUserOpenCore(id, khoa),
    onSettled: lamMoi,
  });
}

export function useXoaUserOpenCore() {
  const lamMoi = useLamMoiDanhBa();
  return useMutation<{ id: string }, ApiError, string>({ mutationFn: (id) => repo.xoaUserOpenCore(id), onSettled: lamMoi });
}

export function useTaoRoleOpenCore() {
  const lamMoi = useLamMoiDanhBa();
  return useMutation<RoleOpenCore, ApiError, ThanRoleOpenCore>({ mutationFn: (than) => repo.taoRoleOpenCore(than), onSettled: lamMoi });
}

export function useSuaRoleOpenCore() {
  const lamMoi = useLamMoiDanhBa();
  return useMutation<RoleOpenCore, ApiError, { id: string; than: ThanRoleOpenCore }>({
    mutationFn: ({ id, than }) => repo.suaRoleOpenCore(id, than),
    onSettled: lamMoi,
  });
}

export function useXoaRoleOpenCore() {
  const lamMoi = useLamMoiDanhBa();
  return useMutation<{ id: string }, ApiError, string>({ mutationFn: (id) => repo.xoaRoleOpenCore(id), onSettled: lamMoi });
}
